// --- React
import React, { useMemo } from 'react';
// --- Utils
import { valueOrDefault } from 'datastage/utils/valueOrDefault';
import { commaSeparate } from 'datastage/utils/commaSeparatedList';
// --- External UI Libraries
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeaderCell,
    TableRow,
} from '@dtci/spellbook/Table';
import Typography from '@dtci/spellbook/Typography';

/**
 * Field group definitions for Summary details
 * Organizes fields into logical collapsible sections
 */
const SUMMARY_FIELD_GROUPS = {
    basicInfo: {
        title: 'Basic Information',
        icon: 'fa-info-circle',
        defaultOpen: true,
        fields: [
            { label: 'Product ID', key: 'productId' },
            { label: 'Title', key: 'title' },
            { label: 'Group Title', key: 'groupTitle' },
            { label: 'GU ID', key: 'guid' },
            { label: 'Product Num', key: 'productNum' },
            { label: 'FO Num', key: 'foNum' },
            { label: 'Consumer Facing Title', key: 'consumerFacingTitle' },
            { label: 'FKA Title', key: 'fkaTitle' },
            { label: 'Status', key: 'status' },
        ],
    },
    episode: {
        title: 'Episode Information',
        icon: 'fa-tv',
        fields: [
            { label: 'Episode Num', key: 'episodeNum' },
            { label: 'Episode Title', key: 'episodeTitle' },
            { label: 'Episode Type', key: 'episodicType' },
            { label: 'Season ID', key: 'seasonId' },
            { label: 'Season', key: 'season' },
        ],
    },
    classification: {
        title: 'Classification',
        icon: 'fa-tags',
        fields: [
            { label: 'Product Category ID', key: 'prodCatgId' },
            { label: 'Product Category', key: 'productCategory' },
            { label: 'Product Class ID', key: 'prodClassId' },
            { label: 'Product Class', key: 'productClass' },
            { label: 'Product Subclass ID', key: 'prodSubclassId' },
            { label: 'Product Subclass', key: 'productSubclass' },
            { label: 'Product Type', key: 'productType' },
            { label: 'Product Source ID', key: 'prodSourceId' },
        ],
    },
    ratings: {
        title: 'Ratings',
        icon: 'fa-star',
        fields: [
            { label: 'MPAA Rating ID', key: 'mpaaRatingId' },
            { label: 'MPAA Rating', key: 'mpaaRating' },
            { label: 'TV Rating ID', key: 'tvRatingId' },
            { label: 'TV Rating', key: 'tvRating' },
            { label: 'TV Origin ID', key: 'tvOriginId' },
            { label: 'TV Origin', key: 'tvOrigin' },
        ],
    },
    language: {
        title: 'Language & Format',
        icon: 'fa-language',
        fields: [
            { label: 'Language Code', key: 'languageCode' },
            { label: 'Language', key: 'language' },
            { label: 'Language Format Code', key: 'languageFormatCode' },
            { label: 'Language Format', key: 'languageFormat' },
            { label: 'Color ID', key: 'colorId' },
            { label: 'Color', key: 'color' },
        ],
    },
    dates: {
        title: 'Important Dates',
        icon: 'fa-calendar',
        fields: [
            { label: 'Release Date', key: 'releaseDate' },
            { label: 'Release Date Est Flag', key: 'releaseDateEstFlag' },
            { label: 'Delivery Date', key: 'deliveryDate' },
            { label: 'Public Domain Date', key: 'publicDomainDate' },
            { label: 'Inserted Date', key: 'insertedDate' },
            { label: 'Updated Date', key: 'updatedDate', isDate: true },
            { label: 'Created At', key: 'createdAt', isDate: true },
            { label: 'Modified At', key: 'modifiedAt', isDate: true },
            { label: 'Deleted At', key: 'deletedAt', isDate: true },
        ],
    },
    flags: {
        title: 'Flags & Status',
        icon: 'fa-flag',
        fields: [
            { label: 'AKA Flag', key: 'akaFlag' },
            { label: 'Composite Flag', key: 'compositeFlag' },
            { label: 'Corp Approval Flag', key: 'corpApprovalFlag' },
            { label: 'Disney Brand Flag', key: 'disneyBrandFlag' },
            { label: 'Platinum Flag', key: 'platinumFlag' },
            { label: 'Public Domain Flag', key: 'publicDomainFlag' },
            { label: 'Disney Plus Original Flag', key: 'disneyPlusOriginalFlag' },
            { label: 'Can Add To Deals', key: 'canAddToDeals', isBoolean: true },
            { label: 'Has Transactional Bonus', key: 'hasTransactionalBonus', isBoolean: true },
        ],
    },
    boxOffice: {
        title: 'Box Office & Financial',
        icon: 'fa-money-bill',
        fields: [
            { label: 'Initial NA Box Office', key: 'initialNaBoxOffice' },
            { label: 'Cumulative NA Box Office', key: 'cumulativeNaBoxOffice' },
            { label: 'Product Cost Number', key: 'prodCostNum' },
            { label: 'GL Acct Num', key: 'glAcctNum' },
        ],
    },
    ownership: {
        title: 'Ownership & Copyright',
        icon: 'fa-copyright',
        fields: [
            { label: 'Copyright Owner', key: 'copyrightOwner' },
            { label: 'Copyright Year', key: 'copyrightYear' },
            { label: 'Presented By ID', key: 'presentedById' },
            { label: 'Presented By', key: 'presentedBy' },
            { label: 'Acquired From', key: 'acquiredFrom' },
            { label: 'Site Access ID', key: 'siteAccessId' },
            { label: 'Site Access', key: 'siteAccess' },
        ],
    },
    identifiers: {
        title: 'External Identifiers',
        icon: 'fa-fingerprint',
        fields: [
            { label: 'MPM Product', key: 'mpmProduct' },
            { label: 'MPM Family', key: 'mpmFamily' },
            { label: 'Product Parent Radar IDs', key: 'productParentRadarIds' },
            { label: 'Foxipedia ID', key: 'foxipediaId' },
            { label: 'Fox WPR ID', key: 'foxFinancialTitleId' },
        ],
    },
    other: {
        title: 'Other Information',
        icon: 'fa-ellipsis',
        fields: [
            { label: 'Comments', key: 'comments' },
        ],
    },
};

/**
 * getSummaryDetails
 *
 * Features:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying summary details.
 *
 * @function getSummaryDetails
 */
export function getSummaryDetails(details) {
    // Define the fields to be displayed in the summary details (flat list for backward compatibility)
    const fields = [
        { label: 'Product ID', key: 'productId' },
        { label: 'Title', key: 'title' },
        { label: 'Group Title', key: 'groupTitle' },
        { label: 'GU ID', key: 'guid' },
        { label: 'Product Num', key: 'productNum' },
        { label: 'Episode Num', key: 'episodeNum' },
        { label: 'Episode Title', key: 'episodeTitle' },
        { label: 'Episode Type', key: 'episodicType' },
        { label: 'FO Num', key: 'foNum' },
        { label: 'AKA Flag', key: 'akaFlag' },
        { label: 'Product Source ID', key: 'prodSourceId' },
        { label: 'Product Category ID', key: 'prodCatgId' },
        { label: 'Product Category', key: 'productCategory' },
        { label: 'Product Class ID', key: 'prodClassId' },
        { label: 'Product Class', key: 'productClass' },
        { label: 'Product Subclass ID', key: 'prodSubclassId' },
        { label: 'Product Subclass', key: 'productSubclass' },
        { label: 'Presented By ID', key: 'presentedById' },
        { label: 'Presented By', key: 'presentedBy' },
        { label: 'Acquired From', key: 'acquiredFrom' },
        { label: 'Composite Flag', key: 'compositeFlag' },
        { label: 'Release Date', key: 'releaseDate' },
        { label: 'Release Date Est Flag', key: 'releaseDateEstFlag' },
        { label: 'Product Cost Number', key: 'prodCostNum' },
        { label: 'GL Acct Num', key: 'glAcctNum' },
        { label: 'Color ID', key: 'colorId' },
        { label: 'Color', key: 'color' },
        { label: 'MPAA Rating ID', key: 'mpaaRatingId' },
        { label: 'MPAA Rating', key: 'mpaaRating' },
        { label: 'Language Code', key: 'languageCode' },
        { label: 'Language', key: 'language' },
        { label: 'Language Format Code', key: 'languageFormatCode' },
        { label: 'Language Format', key: 'languageFormat' },
        { label: 'Delivery Date', key: 'deliveryDate' },
        { label: 'Season ID', key: 'seasonId' },
        { label: 'Season', key: 'season' },
        { label: 'Site Access ID', key: 'siteAccessId' },
        { label: 'Site Access', key: 'siteAccess' },
        { label: 'Copyright Owner', key: 'copyrightOwner' },
        { label: 'Copyright Year', key: 'copyrightYear' },
        { label: 'TV Origin ID', key: 'tvOriginId' },
        { label: 'TV Origin', key: 'tvOrigin' },
        { label: 'TV Rating ID', key: 'tvRatingId' },
        { label: 'TV Rating', key: 'tvRating' },
        { label: 'Corp Approval Flag', key: 'corpApprovalFlag' },
        { label: 'Disney Brand Flag', key: 'disneyBrandFlag' },
        { label: 'Platinum Flag', key: 'platinumFlag' },
        { label: 'Public Domain Flag', key: 'publicDomainFlag' },
        { label: 'Public Domain Date', key: 'publicDomainDate' },
        { label: 'Product Type', key: 'productType' },
        { label: 'Comments', key: 'comments' },
        { label: 'Inserted Date', key: 'insertedDate' },
        { label: 'Updated Date', key: 'updatedDate', isDate: true },
        { label: 'FKA Title', key: 'fkaTitle' },
        { label: 'MPM Product', key: 'mpmProduct' },
        { label: 'MPM Family', key: 'mpmFamily' },
        { label: 'Status', key: 'status' },
        { label: 'Can Add To Deals', key: 'canAddToDeals', isBoolean: true },
        { label: 'Product Parent Radar IDs', key: 'productParentRadarIds' },
        { label: 'Consumer Facing Title', key: 'consumerFacingTitle' },
        { label: 'Initial NA Box Office', key: 'initialNaBoxOffice' },
        { label: 'Cumulative NA Box Office', key: 'cumulativeNaBoxOffice' },
        { label: 'Disney Plus Original Flag', key: 'disneyPlusOriginalFlag' },
        { label: 'Foxipedia ID', key: 'foxipediaId' },
        { label: 'Fox WPR ID', key: 'foxFinancialTitleId' },
        { label: 'Has Transactional Bonus', key: 'hasTransactionalBonus', isBoolean: true },
        { label: 'Created At', key: 'createdAt', isDate: true },
        { label: 'Modified At', key: 'modifiedAt', isDate: true },
        { label: 'Deleted At', key: 'deletedAt', isDate: true },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map(({ label, key, isDate, isBoolean }) => {
            if (isBoolean) {
                // If the field is a boolean, return 'Yes' or 'No'
                return {
                    label,
                    value: details?.[key] ? 'Yes' : 'No',
                };
            }

            // If the field is a date, format it accordingly
            const value = valueOrDefault(details?.[key], isDate ? true : undefined);

            // If the field is a JSON string, parse it and format it as a comma-separated list
            return { label, value };
        });
    } catch (error) {
        console.error('Error processing summary details information ->', error);
        return [];
    }
}

/**
 * getGroupedSummaryDetails
 *
 * Features:
 * Returns summary details organized into collapsible groups.
 * Each group contains related fields for better organization.
 *
 * @function getGroupedSummaryDetails
 * @param {object} details - The product details object
 * @returns {Array} Array of group objects with title, icon, fields, and defaultOpen
 */
export function getGroupedSummaryDetails(details) {
    try {
        return Object.entries(SUMMARY_FIELD_GROUPS).map(([groupKey, group]) => {
            const fields = group.fields.map(({ label, key, isDate, isBoolean }) => {
                let value;
                if (isBoolean) {
                    value = details?.[key] ? 'Yes' : 'No';
                } else {
                    value = valueOrDefault(details?.[key], isDate ? true : undefined);
                }
                return { label, value };
            });

            return {
                key: groupKey,
                title: group.title,
                icon: group.icon,
                defaultOpen: group.defaultOpen || false,
                fields,
            };
        });
    } catch (error) {
        console.error('Error processing grouped summary details ->', error);
        return [];
    }
}


/**
 * getMetadataDetails
 *
 * Fetures:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying metadata details.
 *
 * @param details
 * @returns {{label: *, value: string}[]|*[]}
 */
export function getMetadataDetails(details) {
    // Define the fields to be displayed in the metadata details
    const fields = [
        { label: 'Metadata ID', key: 'metadataId' },
        { label: 'Metadata Copyright', key: 'metadataCopyright' },
        { label: 'Metadata Country Of Origin ID', key: 'metadataCountryOfOriginId' },
        { label: 'Metadata Country Of Origin', key: 'metadataCountryOfOrigin' },
        { label: 'Metadata DMA Eligible', key: 'metadataDmaEligible' },
        { label: 'Metadata EIDR', key: 'metadataEidr' },
        { label: 'Metadata Last Modified', key: 'metadataLastModified' },
        { label: 'Metadata Last Modified By', key: 'metadataLastModifiedBy' },
        { label: 'Metadata Legal Title', key: 'metadataLegalTitle' },
        { label: 'Metadata Locale', key: 'metadataLocale' },
        { label: 'Metadata Localized', key: 'metadataLocalized' },
        { label: 'Metadata Locked', key: 'metadataLocked' },
        { label: 'Metadata Locked By', key: 'metadataLockedBy' },
        { label: 'Metadata Locked Date', key: 'metadataLockedDate' },
        { label: 'Metadata MPM Product ID', key: 'metadataMpmProductId' },
        { label: 'Metadata Original Spoken Language ID', key: 'metadataOriginalSpokenLanguageId' },
        { label: 'Metadata Prettified Legal Title', key: 'metadataPrettifiedLegalTitle' },
        { label: 'Metadata Production Company ID', key: 'metadataProductionCompanyId' },
        { label: 'Metadata Radar Genre', key: 'metadataRadarGenre' },
        { label: 'Metadata Radar Group ID', key: 'metadataRadarGroupId' },
        { label: 'Metadata Radar Language', key: 'metadataRadarLanguage' },
        { label: 'Metadata Radar Language Code', key: 'metadataRadarLanguageCode' },
        { label: 'Metadata Radar Presented By', key: 'metadataRadarPresentedBy' },
        {
            label: 'Metadata Radar Product Ids',
            key: 'metadataRadarProductIds',
            valueFunc: (details) => commaSeparate(JSON.parse(details?.metadataRadarProductIds)),
        },
        { label: 'Metadata Radar Rating', key: 'metadataRadarRating' },
        {
            label: 'Metadata Radar Rating System',
            key: 'metadataRadarRatingSystem',
            valueFunc: (details) =>
                valueOrDefault(
                    details?.metadataRadarRatingSystem
                        ? details.metadataRadarRatingSystem.toString().toUpperCase()
                        : details?.metadataRadarRatingSystem,
                ),
        },
        { label: 'Metadata Radar Synopsis', key: 'metadataRadarSynopsis' },
        { label: 'Metadata Radar Release Date', key: 'metadataRadarReleaseDate' },
        { label: 'Metadata Radar Runtime', key: 'metadataRadarRunTime' },
        { label: 'Metadata Rating Type', key: 'metadataRatingType' },
        { label: 'Metadata Title', key: 'metadataTitle' },
        { label: 'Metadata Video Type', key: 'metadataVideoType' },
        { label: 'Metadata Keywords', key: 'metadataKeywords' },
        { label: 'Metadata Short Synopsis', key: 'metadataShortSynopsis' },
        { label: 'Metadata Medium Synopsis', key: 'metadataMediumSynopsis' },
        { label: 'Metadata Runtime Seconds', key: 'metadataRuntimeSeconds' },
        { label: 'Metadata Language', key: 'metadataLanguage' },
        { label: 'Metadata Territory', key: 'metadataTerritory' },
        { label: 'Metadata Product Type', key: 'metadataProductType' },
        { label: 'Metadata Account', key: 'metadataAccount' },
        { label: 'Metadata Localized Fields', key: 'metadataLocalizedFields' },
        { label: 'Metadata Product Class', key: 'metadataProductClass' },
        { label: 'Metadata Production Company', key: 'metadataProductionCompany' },
        { label: 'Metadata Bolt Genre', key: 'metadataBoltGenre' },
        { label: 'Metadata Bolt US Rating', key: 'metadataBoltUsRating' },
        { label: 'Metadata Bolt CAN Rating RDCQ', key: 'metadataBoltCanadianRatingRDCQ' },
        { label: 'Metadata Bolt CAN Rating CHVRS', key: 'metadataBoltCanadianRatingCHVRS' },
        {
            label: 'Metadata Bolt US First Physical Release Date',
            key: 'metadataBoltUsFirstPhysicalReleaseDate',
            isDate: true,
        },
        {
            label: 'Metadata Bolt US Theatrical Release Date',
            key: 'metadataBoltUsTheatricalReleaseDate',
            isDate: true,
        },
        { label: 'Metadata Actual Runtime', key: 'metadataActualRunTime' },
        { label: 'Metadata Number Of Episodes', key: 'metadataNumberOfEpisodes' },
        { label: 'Metadata US Release Date', key: 'metadataUsReleaseDate' },
        { label: 'Metadata CA Release Date', key: 'metadataCaReleaseDate' },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map((field) => {
            const value = field.valueFunc
                ? field.valueFunc(details)
                : valueOrDefault(details?.[field.key], field.isDate ? true : undefined);
            return { label: field.label, value };
        });
    } catch (error) {
        console.log('Error processing metadata details information ->', error);
        return [];
    }
}

/**
 * getProspectDetails
 *
 * Fetures:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying prospect details.
 *
 * @param details
 * @returns {{label: *, value: string}[]|*[]}
 */
export function getProspectDetails(details) {
    // Define the fields to be displayed in the prospect details
    const fields = [
        { label: 'PS ID', key: 'psId' },
        { label: 'PS Title', key: 'psTitle' },
        { label: 'PS Class', key: 'psClass' },
        { label: 'PS Keywords', key: 'psKeywords' },
        { label: 'PS Studio PlanBo', key: 'psStudioPlanBo' },
        { label: 'PS 3d Adj Studio Plan Bo', key: 'ps3dAdjStudioPlanBo' },
        { label: 'PS Reported From Bvpd Bo', key: 'psReportedFromBvpdBo' },
        { label: 'PS Actual Bo', key: 'psActualBo' },
        { label: 'PS Audited Bo', key: 'psAuditedBo' },
        { label: 'PS Std Plan Rentals', key: 'psStdPlanRentals' },
        { label: 'PS Audited Rentals', key: 'psAuditedRentals' },
        { label: 'PS US Bo', key: 'psUsBo' },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map(({ label, key }) => ({
            label,
            value: valueOrDefault(details?.[key]),
        }));
    } catch (error) {
        console.error('Error processing prospect details information ->', error);
        return [];
    }
}

/**
 * getSpartaDetails
 *
 * Fetures:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying sparta details.
 *
 * @param details
 * @returns {{label: *, value: string}[]|*[]}
 */
export function getSpartaDetails(details) {
    // Define the fields to be displayed in the sparta details
    const fields = [
        { label: 'Sparta US Theatrical Release Date', key: 'spartaUsTheatricalReleaseDate' },
        { label: 'Sparta Earliest Release Date', key: 'spartaEarliestReleaseDate' },
        {
            label: 'Sparta Studio Reported Gross Usd For US',
            key: 'spartaStudioReportedGrossUsdForUs',
        },
        {
            label: 'Sparta Studio Reported Gross Usd For CA',
            key: 'spartaStudioReportedGrossUsdForCa',
        },
        {
            label: 'Sparta Studio Reported Gross Usd For Domestic',
            key: 'spartaStudioReportedGrossUsdForDomestic',
        },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map(({ label, key }) => ({
            label,
            value: valueOrDefault(details?.[key]),
        }));
    } catch (error) {
        console.error('Error processing sparta details information ->', error);
        return [];
    }
}

/**
 * getNielsenDetails
 *
 * Fetures:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying nielsen details.
 *
 * @param details
 * @returns {{label: *, value: string}[]|*[]}
 */
export function getNielsenDetails(details) {
    // Define the fields to be displayed in the nielsen details
    const fields = [
        { label: 'Nielsen Season Dates Load Status', key: 'nielsenSeasonDatesLoadStatus' },
        { label: 'Nielsen Season Dates Load Error Msg', key: 'nielsenSeasonDatesLoadErrorMsg' },
        {
            label: 'Nielsen Season Dates Load Status Change Date',
            key: 'nielsenSeasonDatesloadStatusChangeDate',
        },
        { label: 'Nielsen Season End Date', key: 'nielsenSeasonEndDate' },
        { label: 'Nielsen Season Start Date', key: 'nielsenSeasonStartDate' },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map(({ label, key }) => ({
            label,
            value: valueOrDefault(details?.[key]),
        }));
    } catch (error) {
        console.error('Error processing nielsen details information ->', error);
        return [];
    }
}

/**
 * getLoadDetails
 *
 * Fetures:
 * This function takes a details object and returns an array of objects
 * containing label and value pairs for displaying load details.
 *
 * @param details
 * @returns {{label: *, value: string}[]|*[]}
 */
export function getLoadDetails(details) {
    // Define the fields to be displayed in the load details
    const fields = [
        { label: 'Load Status', key: 'loadStatus' },
        { label: 'Load Status Change Date', key: 'loadStatusChangeDate', isDate: true },
        { label: 'Load Error Msg', key: 'loadErrorMsg' },
    ];

    try {
        // Map the fields to their corresponding values from the details object
        return fields.map(({ label, key, isDate }) => ({
            label,
            value: valueOrDefault(details?.[key], isDate ? true : undefined),
        }));
    } catch (error) {
        console.error('Error processing load details information ->', error);
        return [];
    }
}

/**
 * DetailsTableSection
 *
 * Fetures:
 * This component renders a table section for displaying details such as Cast, Crew, or Foreign Titles.
 *
 * @param title
 * @param headers
 * @param keys
 * @param jsonData
 * @returns {Element}
 * @constructor
 */
function DetailsTableSection({ title, headers, keys, jsonData }) {
    // Memoize the parsed JSON data to avoid unnecessary re-parsing
    const data = useMemo(() => {
        try {
            // Parse the JSON data and return it
            return JSON.parse(jsonData || '[]');
        } catch (error) {
            console.warn(`Error parsing ${title} JSON:`, error);
            return [];
        }
    }, [jsonData]);

    return (
        <div style={{ marginBottom: '2rem' }}>
            {/* Render the title of the section */}
            <Typography variant="T20" bold>
                {title}:
            </Typography>
            {/* Render the table with headers and data */}
            <Table>
                <TableHead>
                    <TableRow>
                        {/* Render the headers of the table */}
                        {headers.map((header, idx) => (
                            <TableHeaderCell key={idx}>{header}</TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHead>
                {/* Render the body of the table */}
                <TableBody>
                    {/* If data is available, map through it and render each row */}
                    {data.length > 0 ? (
                        data.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>
                                {keys.map((key, cellIndex) => (
                                    <TableCell key={cellIndex}>{row[key] ?? '--'}</TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        // If no data is available, render a row with placeholders
                        <TableRow>
                            {headers.map((_, idx) => (
                                <TableCell key={idx}>--</TableCell>
                            ))}
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </div>
    );
}

/**
 * MetadataTables
 *
 * Fetures:
 * This component renders multiple metadata tables including Cast, Crew, and Foreign Titles.
 *
 * @param details
 * @returns {Element}
 * @constructor
 */
export function MetadataTables({ details }) {
    // Define the sections to be displayed in the metadata tables
    const sections = [
        {
            title: 'Cast',
            headers: ['Locale', 'First Name', 'Last Name', 'Character Name', 'Billing Order'],
            keys: ['locale', 'firstName', 'lastName', 'characterName', 'billingOrder'],
            jsonData: details?.metadataCast,
        },
        {
            title: 'Crew',
            headers: ['Locale', 'First Name', 'Last Name', 'Role', 'Billing Order'],
            keys: ['locale', 'firstName', 'lastName', 'role', 'billingOrder'],
            jsonData: details?.metadataCrew,
        },
        {
            title: 'Foreign Titles',
            headers: ['Locale', 'Title'],
            keys: ['locale', 'title'],
            jsonData: details?.metadataForeignTitles,
        },
    ];

    return (
        <>
            {/* Render each section of the metadata tables */}
            {sections.map((section, idx) => (
                <DetailsTableSection key={idx} {...section} />
            ))}
        </>
    );
}
